import { useState, useEffect, useMemo } from "react";
import { useParams, Link, useLocation } from "react-router-dom";
import { CircleCheckBig, Star, Award } from "lucide-react";
import { social } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import RankBadge from "../components/social/RankBadge";
import Avatar from "../components/common/Avatar";
import HistoryCard from "../components/social/HistoryCard";
import ActivityDetailModal from "../components/social/ActivityDetailModal";
import Pagination from "../components/common/Pagination";
import LoadingOverlay from "../components/LoadingOverlay";

export default function ProfilePage() {
  const { id } = useParams();
  const location = useLocation();
  const { user: currentUser, switchUser, seedUsers } = useAuth();
  const userId = id || currentUser?.id;
  const isSelf = !id || id === currentUser?.id;

  const [profile, setProfile] = useState(null);
  const [ratings, setRatings] = useState({ ratings: [], pagination: {} });
  const [cpData, setCpData] = useState(null);
  const [historyPage, setHistoryPage] = useState(1);
  const [activityTab, setActivityTab] = useState("contributions");
  const [selectedItem, setSelectedItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const ITEMS_PER_PAGE = 10;

  useEffect(() => {
    setLoading(true);
    Promise.all([
      social.getUserProfile(userId),
      social.getUserRatings(userId, { page: 1, limit: 50 }),
      social.getUserCp(userId),
    ]).then(([p, r, cp]) => {
      setProfile(p);
      setRatings(r);
      setCpData(cp);
      setHistoryPage(1);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [userId, location.key]);

  // Group ratings and CP entries by response_id so each exchange appears once
  const unifiedHistory = useMemo(() => {
    const byResponseId = {};

    for (const r of ratings.ratings) {
      const key = r.response_id || `rating-only-${r.id}`;
      if (!byResponseId[key]) byResponseId[key] = { rating: null, cp: null };
      byResponseId[key].rating = r;
    }

    if (cpData?.log) {
      for (const entry of cpData.log) {
        const key = entry.response_id || `cp-only-${entry.id}`;
        if (!byResponseId[key]) byResponseId[key] = { rating: null, cp: null };
        byResponseId[key].cp = entry;
      }
    }

    return Object.entries(byResponseId)
      .map(([key, data]) => ({
        id: key,
        created_at: data.rating?.created_at || data.cp?.created_at,
        rating: data.rating,
        cp: data.cp,
      }))
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  }, [ratings.ratings, cpData]);

  const filteredHistory = unifiedHistory.filter((item) =>
    activityTab === "contributions" ? item.cp : !item.cp
  );

  const totalPages = Math.max(1, Math.ceil(filteredHistory.length / ITEMS_PER_PAGE));
  const paginatedHistory = filteredHistory.slice(
    (historyPage - 1) * ITEMS_PER_PAGE,
    historyPage * ITEMS_PER_PAGE
  );

  if (loading) return <LoadingOverlay />;
  if (!profile) return <p className="text-coral py-8">User not found</p>;

  return (
    <div>
      {isSelf && (
        <div className="mb-4 md:hidden">
          <select
            value={currentUser?.id || ""}
            onChange={(e) => {
              const selected = seedUsers.find((u) => u.id === e.target.value);
              if (selected) switchUser(selected);
            }}
            className="px-3 py-2 bg-cream border border-stone rounded-md text-sm text-walnut font-medium focus:outline-none focus:ring-2 focus:ring-terracotta/30"
          >
            {seedUsers.map((u) => (
              <option key={u.id} value={u.id}>{u.display_name}</option>
            ))}
          </select>
        </div>
      )}
      <div className="text-center mb-8">
        <div className="flex justify-center mb-3">
          <Avatar user={profile} size="lg" />
        </div>
        <div className="flex items-center justify-center gap-2 mb-1">
          <span className="font-display text-2xl font-bold text-walnut">{profile.display_name}</span>
          {profile.is_verified && <CircleCheckBig size={20} className="text-terracotta" />}
        </div>
        <div className="flex items-center justify-center gap-3 mb-2">
          <RankBadge rank={profile.rank} />
          <span className="text-sm font-semibold text-amber">{profile.community_points} CP</span>
          {profile.rating_avg != null && (
            <span className="inline-flex items-center gap-1 text-sm text-walnut-light">
              <Star size={14} className="fill-amber text-amber" />
              {profile.rating_avg.toFixed(1)} ({profile.rating_count})
            </span>
          )}
        </div>
        {profile.bio && <p className="text-sm text-walnut-light max-w-md mx-auto">{profile.bio}</p>}
      </div>
      {profile.rank_history && profile.rank_history.length > 0 && (
        <div className="mb-8">
          <h2 className="font-display text-lg font-semibold text-walnut mb-3">Rank History</h2>
          <div className="space-y-2 border-l-2 border-stone pl-4">
            {profile.rank_history.map((rh, i) => (
              <div key={i} className="flex items-center gap-3">
                <RankBadge rank={rh.rank} />
                <span className="text-xs text-walnut-light">
                  {new Date(rh.achieved_at).toLocaleDateString()}
                </span>
              </div>
            ))}
          </div>
          <Link
            to={`/certs/rank/${userId}`}
            className="inline-flex items-center gap-1.5 mt-3 text-sm font-medium text-terracotta hover:text-terracotta-dark transition-colors"
          >
            <Award size={16} />
            View Rank Certificate
          </Link>
        </div>
      )}
      <div className="flex gap-1 mb-4 bg-cream rounded-lg p-1">
        {[
          { key: "contributions", label: "Contributions" },
          { key: "requests", label: "Requests" },
        ].map(({ key, label }) => (
          <button
            key={key}
            onClick={() => { setActivityTab(key); setHistoryPage(1); }}
            className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activityTab === key ? "bg-terracotta text-white" : "text-walnut-light hover:text-walnut"
            }`}
          >
            {label}
          </button>
        ))}
      </div>
      <div className="space-y-3">
        {paginatedHistory.length === 0 ? (
          <p className="text-walnut-light py-4">No activity yet.</p>
        ) : (
          paginatedHistory.map((item) => (
            <HistoryCard
              key={item.id}
              item={item}
              onClick={() => setSelectedItem(item)}
            />
          ))
        )}
        <Pagination
          page={historyPage}
          totalPages={totalPages}
          onPageChange={setHistoryPage}
        />
      </div>
      {selectedItem && (
        <ActivityDetailModal
          item={selectedItem}
          onClose={() => setSelectedItem(null)}
        />
      )}
    </div>
  );
}
